import { Avatar, Grid } from '@mui/material';
import { IPostComment } from '_interfaces/postComment.interface';
import { addPostCommentAtom, selectedProjectAtom } from 'atoms';
import DefaultAvatar from 'components/default-avatar';
import { ICustomMentionData } from 'components/inputs/draft-js-input';
import { useAtomValue, useSetAtom } from 'jotai';
import { FC, RefObject, useCallback, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useGetMeQuery } from 'redux-store/api/auth';
import { getImageBySize } from 'utils/helpers';
import Post from '../post';
import { postReplyStyles } from './styles/styles';

interface IReplyProps {
  setOpen: (open: boolean) => void;
  companyId: string;
  projectId: number;
  comment?: IPostComment;
  replyRef?: RefObject<HTMLDivElement | null>;
  mentionOptions: ICustomMentionData[];
  uploadPath?: string;
  onCreateComment?: (body: Partial<IPostComment>) => void;
  onUpdateComment?: (body: Partial<IPostComment>) => void;
}

const Reply: FC<IReplyProps> = ({
  companyId,
  projectId,
  setOpen,
  comment,
  replyRef,
  mentionOptions,
  uploadPath,
  onCreateComment,
  onUpdateComment,
}) => {
  const { data: userMe } = useGetMeQuery();
  const addComment = useSetAtom(addPostCommentAtom);
  const project = useAtomValue(selectedProjectAtom);

  const replyToProfile = useMemo(() => {
    if (!comment) return undefined;
    const foundProfile = project?.profiles?.find(
      (p) => p.userId.toString() === comment.createdBy.userId
    );
    if (foundProfile) {
      return foundProfile;
    }
    const nameParts = (comment.createdBy.displayName || '').split(' ');
    return {
      id: parseInt(comment.createdBy.userId, 10) || 0,
      userId: parseInt(comment.createdBy.userId, 10) || 0,
      firstName: nameParts[0] || '',
      lastName: nameParts.slice(1).join(' ') || '',
      avatar: comment.createdBy.avatarUrl || null,
      role: '',
      email: '',
    };
  }, [comment, project?.profiles]);

  const handleCreateComment = useCallback(
    (body: Partial<IPostComment>) => {
      const commentData = comment
        ? {
            ...body,
            replyToCommentId: comment.id,
            rootCommentId: comment.rootCommentId || comment.id,
          }
        : body;

      if (onCreateComment) {
        onCreateComment(commentData);
      } else if (comment?.parentPath) {
        addComment({
          data: commentData,
          parentPath: comment.parentPath,
          user: {
            userId: userMe?.id?.toString() || '',
            displayName: `${userMe?.firstName || ''} ${userMe?.lastName || ''}`.trim(),
            avatarUrl: userMe?.avatar || '',
          },
        });
        setOpen(false);
      }
    },
    [onCreateComment, comment, addComment, setOpen, userMe]
  );

  const [searchParams] = useSearchParams();

  const getCustomInputStyles = useCallback(
    (isError: boolean) => ({
      '.mnw6qvm': {
        borderRadius: '12px',
        marginTop: '12px',
        filter:
          'drop-shadow(0px 4px 10px rgba(35, 37, 47, 0.1)) drop-shadow(1px 2px 2px rgba(35, 37, 47, 0.04))',
        boxShadow: 'none',
      },
      '.DraftEditor-root': {
        height: !comment && searchParams.has('postId') ? 'unset' : '120px',
        minHeight: 'unset',
      },
      '.DraftEditor-editorContainer': {
        height: !comment && searchParams.has('postId') ? 'auto' : '120px',
        maxHeight: !comment && searchParams.has('postId') ? '120px' : 'unset',
        overflowY: 'auto',
        borderRadius: '8px',
        outline: isError ? '1px solid #d32f2f' : '1px solid #EDEFF7',
        fontFamily:
          "'Inter', 'Apple Color Emoji', 'Segoe UI Emoji', 'Noto Color Emoji', 'Twemoji', sans-serif",
        div: { boxSizing: 'border-box' },
        '&:hover': {
          outline: '1px solid #EDEFF7',
        },
        '&:focus-within': {
          outline: '1px solid #EDEFF7',
        },
      },
    }),
    [comment, searchParams]
  );

  const initials = `${userMe?.firstName?.[0]}${userMe?.lastName?.[0]}`;

  useEffect(() => {
    replyRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [replyRef]);

  return (
    <Grid sx={[postReplyStyles.container, comment && postReplyStyles.gap]} ref={replyRef}>
      <Grid sx={postReplyStyles.profileContainer}>
        {userMe?.avatar ? (
          <>
            <Avatar
              alt="avatar"
              sx={postReplyStyles.customAvatar}
              src={getImageBySize(userMe?.avatar, 40, 40)}
              variant="square"
            />
          </>
        ) : (
          <DefaultAvatar
            initials={initials}
            alt="avatar"
            variant="square"
            sx={{ borderRadius: '8px', width: '40px', height: '40px' }}
          />
        )}
      </Grid>
      <Grid sx={postReplyStyles.inputContainer}>
        <Post
          isExpanded={true}
          actionType="CREATE_COMMENT"
          companyId={companyId}
          projectId={projectId}
          getCustomInputStyles={getCustomInputStyles}
          setOpen={setOpen}
          onCreateComment={handleCreateComment}
          onUpdateComment={onUpdateComment}
          uploadPath={uploadPath}
          replyToProfile={replyToProfile}
          mentionOptions={mentionOptions}
        />
      </Grid>
    </Grid>
  );
};

export default Reply;
